import { inject, Injectable } from '@angular/core';
import { ShepherdService } from 'angular-shepherd';
import Step from 'shepherd.js/src/types/step';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import {
  BehaviorSubject,
  filter,
  finalize,
  from,
  Observable,
  share,
  take,
} from 'rxjs';
import Shepherd from 'shepherd.js';
import { OnboardingTourName } from '../../enums';
import { LocalStorageService } from '../local-storage.service';
import { Platform } from '@angular/cdk/platform';
import { getCoords, scrollWithCallback } from '../../utils';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private translateService = inject(TranslateService);
  private localStorageService = inject(LocalStorageService);
  private platform = inject(Platform);
  private router = inject(Router);

  private isMobile = this.platform.IOS || this.platform.ANDROID;
  private tourEventTypes = Object.values(TourEventType);
  public currentAvailableTour = new BehaviorSubject<OnboardingTour | undefined>(
    undefined
  );
  private shepherdServices: ShepherdService[] = [];
  private defaultStepOptions: Step.StepOptions = {
    classes: 'ling-onboarding',
    scrollTo: true,
    scrollToHandler: (element) => {
      if (this.isMobile) {
        clearAllBodyScrollLocks();
      }
      const elementCoords = getCoords(element);
      scrollWithCallback(
        {
          top:
            elementCoords.top - window.innerHeight + element.offsetHeight + 10,
          left: 0,
          behavior: 'smooth',
        },
        () => {
          if (!this.isMobile) {
            disableBodyScroll(document.documentElement);
          }
        }
      );
    },
    cancelIcon: { enabled: true },
    modalOverlayOpeningPadding: 10,
    modalOverlayOpeningRadius: 10,
    popperOptions: {
      modifiers: [{ name: 'offset', options: { offset: [0, 26] } }],
    },
  };

  private getFirstButtons = (shepherdInstance: ShepherdService) => [
    {
      text: this.translateService.instant('onboarding.default.nextStepButton'),
      action: () => shepherdInstance.next(),
    },
  ];
  private getInProgressButtons = (shepherdInstance: ShepherdService) => [
    {
      text: this.translateService.instant(
        'onboarding.default.previousStepButton'
      ),
      action: () => shepherdInstance.back(),
    },
    {
      text: this.translateService.instant('onboarding.default.nextStepButton'),
      action: () => shepherdInstance.next(),
    },
  ];
  private getLastButtons = (shepherdInstance: ShepherdService) => [
    {
      text: this.translateService.instant(
        'onboarding.default.previousStepButton'
      ),
      action: () => shepherdInstance.back(),
    },
    {
      text: this.translateService.instant(
        'onboarding.default.completeTourButton'
      ),
      action: () => shepherdInstance.complete(),
    },
  ];

  get isActive() {
    return this.currentShepherdInstance?.tourObject?.isActive();
  }

  private get currentShepherdInstance() {
    return this.shepherdServices.at(-1);
  }

  get currentTour() {
    return this.currentShepherdInstance;
  }

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.currentAvailableTour.next(undefined);
        this.currentTour?.cancel();
      });
  }

  private buildSteps(
    shepherdInstance: ShepherdService,
    steps: OnboardingStep[]
  ): Step.StepOptions[] {
    return steps.map((step, index) => {
      let shepherdStep: Step.StepOptions = {
        title: this.translateService.instant(step.title as string),
        text: this.translateService.instant(step.content as string),
      };
      if (index === 0) {
        shepherdStep.buttons = this.getFirstButtons(shepherdInstance);
      }
      if (index > 0) {
        shepherdStep.buttons = this.getInProgressButtons(shepherdInstance);
      }
      if (index === steps.length - 1) {
        shepherdStep.buttons = this.getLastButtons(shepherdInstance);
      }

      if (step.attachTo) {
        shepherdStep.attachTo = {
          element: step.attachTo,
          on: step.position ?? 'auto',
        };
      }

      return shepherdStep;
    });
  }

  private setScrollBlockEventListeners(shepherdInstance: ShepherdService) {
    if (this.isMobile) {
      return;
    }
    [TourEventType.TourCancel, TourEventType.TourComplete].forEach((event) =>
      shepherdInstance.tourObject.on(event, () => {
        clearAllBodyScrollLocks();
      })
    );
    shepherdInstance.tourObject.on(TourEventType.StepShow, () => {
      disableBodyScroll(document.documentElement);
    });
  }

  registerCurrentAvailableTour(tour?: OnboardingTour) {
    this.currentAvailableTour.next(tour);
  }

  startCurrentAvailableTour() {
    if (this.currentAvailableTour.value) {
      this.startTour(this.currentAvailableTour.value);
    }
  }

  startCurrentAvailableTourIfNotMuted() {
    if (
      !this.localStorageService.isOnboardingTourMuted(
        this.currentAvailableTour.value?.name
      )
    ) {
      this.startCurrentAvailableTour();
    }
  }

  private startTour(tour: OnboardingTour) {
    const wasActive = !!this.isActive;
    const previousInstance = this.currentShepherdInstance;
    const newShepherdInstance = new ShepherdService();
    this.shepherdServices.push(newShepherdInstance);

    const tourObservable = new Observable<TourEvent>((observer) => {
      newShepherdInstance.defaultStepOptions = this.defaultStepOptions;
      newShepherdInstance.modal = !this.isMobile;
      newShepherdInstance.tourName = tour.name;
      // if there is a mat dialog than place steps in dialog context
      let cdkOverlayContainer = document.documentElement.querySelector(
        '.cdk-overlay-container'
      );

      if (!cdkOverlayContainer) {
        const cdkOverlay = document.createElement('div');
        cdkOverlay.classList.add('cdk-overlay-container');
        document.body.appendChild(cdkOverlay);
        cdkOverlayContainer = cdkOverlay;
      }

      const stepsContainer = cdkOverlayContainer;

      newShepherdInstance.stepsContainer = stepsContainer;
      newShepherdInstance.modalContainer = stepsContainer;

      newShepherdInstance.addSteps(
        this.buildSteps(newShepherdInstance, tour.steps)
      );
      this.setScrollBlockEventListeners(newShepherdInstance);
      this.tourEventTypes.forEach((eventName) =>
        newShepherdInstance.tourObject.on(eventName, (event: any) => {
          observer.next({ ...event, event: eventName });
        })
      );
      [TourEventType.TourCancel, TourEventType.TourComplete].forEach(
        (eventName) =>
          newShepherdInstance.tourObject.on(eventName, () => {
            this.tourEventTypes.forEach((eventType) => {
              newShepherdInstance.tourObject.off(eventType);
            });
            observer.complete();
          })
      );
      newShepherdInstance.start();
    }).pipe(
      finalize(() => {
        this.localStorageService.muteOnboardingTour(tour.name);
        this.shepherdServices.splice(
          this.shepherdServices.indexOf(newShepherdInstance),
          1
        );
      }),
      share()
    );

    if (wasActive && previousInstance) {
      this._pauseInstanceUntil(previousInstance, tourObservable, {
        onComplete: true,
      });
    }

    tourObservable.subscribe();

    return tourObservable;
  }

  private _pauseInstanceUntil(
    shepherdInstance: ShepherdService,
    observableInput: Parameters<typeof from>[0],
    config: { onComplete: boolean } = { onComplete: false }
  ) {
    const cb = () => {
      setTimeout(() => {
        const currentStepId = shepherdInstance.tourObject.getCurrentStep()!.id;
        shepherdInstance.tourObject.start();
        shepherdInstance.tourObject.show(currentStepId);
      });
    };

    shepherdInstance.tourObject.hide();

    setTimeout(() => {
      if (config.onComplete) {
        from(observableInput).pipe().subscribe({
          complete: cb,
        });
      } else {
        from(observableInput).pipe(take(1)).subscribe({
          next: cb,
        });
      }
    }, 0);
  }

  pauseCurrentUntil(
    observableInput: Parameters<typeof from>[0],
    config: { onComplete: boolean } = { onComplete: false }
  ) {
    if (!this.currentShepherdInstance) {
      return;
    }
    this._pauseInstanceUntil(
      this.currentShepherdInstance,
      observableInput,
      config
    );
  }
}

export interface OnboardingTour {
  name: OnboardingTourName;
  steps: OnboardingStep[];
}

export interface OnboardingStep {
  title?: Step.StepOptions['title'];
  content: Step.StepOptions['text'];
  attachTo?: Step.StepOptionsAttachTo['element'];
  position?: Step.StepOptionsAttachTo['on'];
}

export interface TourEvent {
  step?: Shepherd.Step;
  previous?: Shepherd.Step;
  tour: Shepherd.Tour;
  index?: number;
  event: TourEventType;
}

export enum TourEventType {
  StepHide = 'hide',
  StepShow = 'show',
  TourStart = 'start',
  TourActive = 'active',
  TourInactive = 'inactive',
  TourComplete = 'complete',
  TourCancel = 'cancel',
}
